import { Component, OnDestroy, AfterViewInit, NgZone } from "@angular/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import moment from "moment";
import am4lang_pt_PT from "@amcharts/amcharts4/lang/pt_PT";
import am4lang_en_EN from "@amcharts/amcharts4/lang/en_US";
import { Contract } from "@edp-components/edp-contract";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { takeUntil } from "rxjs/operators";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Subject, zip } from "rxjs";
import { getConsumptions } from "@edp-store/selectors/consumption.selectors";
import { IConsumptions } from "./edp-conta-certa.model";
import { GetConsumptions } from "@edp-store/actions/consumption.actions";
import { getSimulation } from "@edp-store/selectors/dashboard.selectors";
import { Simulation } from "../edp-widget-consumptions/edp-consumptions.models";
import { GetSimulation } from "@edp-store/actions/dashboard.actions";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { EdpContaCertaModalComponent } from "@edp-components/edp-conta-certa-modal/edp-conta-certa-modal.component";
import { EdpModalService } from "@edp-components/edp-modal";
import { LanguageService } from "@edp-services/language.service";
import { LocaleEnum } from "@edp-enums/language.enum";

am4core.options.autoSetClassName = true;
am4core.useTheme(am4themes_animated);
am4core.addLicense("CH120763846");

@Component({
    selector: "edp-widget-conta-certa",
    templateUrl: "./edp-widget-conta-certa.component.html",
    styleUrls: ["./edp-widget-conta-certa.component.scss"],
})
export class EdpWidgetContaCertaComponent implements AfterViewInit, OnDestroy {
    private unsubscribe = new Subject();
    public contract: Contract;
    public chart: am4charts.XYChart;
    public consumptionsObject: IConsumptions = null;
    public month = 11;
    public valueContaCerta = 0;
    public valueContaCertaMedia = 0;
    public valueContaCertaAcerto = "0";
    public chartColor = "#7C9599";
    public chartColorGreen = "#225E66";
    public canPaint = false;
    public label: string;
    public labelMonthTwelveTitle: string;
    public labelMonthTwelveValueDescription: string;
    public labelMiddleMonths: string;
    public labelMiddleMonthsSecondLabel: string;
    public finalValuePositive = true;
    public finalValueAcertoPositive = true;
    public lastMonthContaCerta: string;
    public simulation: Simulation;
    public monthsWithoutReadings: number;
    public startEstimatedMonth: string;
    public startEstimatedMonthLabel: string;
    public hasEstimatedMonths = false;
    public endEstimatedMonth: string;
    public endEstimatedMonthLabel: string;
    public loading = true;
    public allEstimated: boolean;
    public error: boolean;

    constructor(
        private zone: NgZone,
        private edpModalService: EdpModalService,
        private store: Store<IAppState>,
        private languageService: LanguageService
    ) {
        this.store.pipe(takeUntil(this.unsubscribe), select(getContract)).subscribe(contract => {
            this.contract = contract;
            this.valueContaCerta = this.contract.ContaCertaValue ? parseInt(this.contract.ContaCertaValue, 0) : 0;
        });
    }

    public ngAfterViewInit() {
        this.store.dispatch(new GetConsumptions(this.contract));
        this.store.dispatch(new GetSimulation(this.contract, true));
        // wait until all observables have emitted a value then emit all as an array
        zip(this.store.select(getSimulation), this.store.select(getConsumptions)).subscribe(([simulation, consumptions]) => {
            this.simulation = simulation;
            this.consumptionsObject = consumptions;
            this.setLabels();
        });
    }

    public setLabels() {
        let missingMonths = 0;
        if (
            this.consumptionsObject &&
            this.simulation &&
            this.consumptionsObject.currentMonthContaCerta > 0 && this.consumptionsObject.currentMonthContaCerta < 13
        ) {
            this.error = false;
            this.month = this.consumptionsObject.currentMonthContaCerta;
            this.valueContaCertaAcerto = this.simulation.simulationValue;
            this.valueContaCertaMedia = this.consumptionsObject.averageContaCerta;
            if (this.valueContaCertaMedia) {
                this.valueContaCertaMedia = parseFloat(this.valueContaCertaMedia.toFixed(2));
            }
            this.lastMonthContaCerta = moment(this.consumptionsObject.endContaCerta)
                .locale(this.languageService.getLocale())
                .format("MMMM");
            missingMonths = moment(this.consumptionsObject.endContaCerta)
                .subtract(1, "months")
                .diff(moment(), "months");
            this.valueContaCertaAcerto = (parseFloat(this.valueContaCertaAcerto) - this.valueContaCerta * missingMonths).toString();
            this.valueContaCertaAcerto = parseFloat(this.valueContaCertaAcerto).toFixed(2);

            this.finalValueAcertoPositive = this.valueContaCertaAcerto > "0" ? true : false;
            if (!this.finalValueAcertoPositive) {
                this.valueContaCertaAcerto = this.valueContaCertaAcerto.replace("-", "");
            }
            this.finalValuePositive = this.valueContaCertaMedia > this.valueContaCerta ? true : false;

            this.allEstimated = this.consumptionsObject.startContaCerta > this.consumptionsObject.startEstimatedMonth;
            this.monthsWithoutReadings = moment().diff(this.consumptionsObject.startEstimatedMonth, "months");
            this.startEstimatedMonth = moment(this.consumptionsObject.startEstimatedMonth)
                .locale(this.languageService.getLocale())
                .format("MMM");
            this.startEstimatedMonthLabel = moment(this.consumptionsObject.startEstimatedMonth)
                .locale(this.languageService.getLocale())
                .format("MMMM");
            this.endEstimatedMonth = moment()
                .locale(this.languageService.getLocale())
                .format("MMM");
            this.endEstimatedMonthLabel = moment()
                .locale(this.languageService.getLocale())
                .format("MMMM");
            if (this.monthsWithoutReadings > 2) {
                this.hasEstimatedMonths = true;
            }
            this.chartConstruction();
            this.setLabelbyMonth();
            this.loading = false;

            if (localStorage.getItem("conta-certa-deeplink") === "MONTH_PAYMENT_CC") {
                localStorage.removeItem("conta-certa-deeplink");
                if (this.month >= 3 && this.month < 11 && !this.hasEstimatedMonths) {
                    this.reviewMonthPayment();
                }
            }
        } else {
            this.month = 1;
            this.error = true;
            this.chartConstruction();
            this.setLabelbyMonth();
            this.loading = false;
        }
    }

    getLocale(chart): void {
        if (this.languageService.getLocale() !== LocaleEnum.pt) {
            chart.language.locale = am4lang_en_EN;
            chart.dateFormatter.language.locale = am4lang_en_EN;
        } else {
            chart.language.locale = am4lang_pt_PT;
            chart.dateFormatter.language.locale = am4lang_pt_PT;
        }
    }

    public chartConstruction() {
        this.zone.runOutsideAngular(() => {
            // Create chart instance
            const chart = am4core.create("graphicContaCerta", am4charts.XYChart);
            chart.hiddenState.properties.opacity = 0; // this creates initial fade-in
            chart.height = am4core.percent(100);
            chart.paddingTop = 20;

            // Add data
            const data = this.getData();
            chart.data = data;

            this.getLocale(chart);

            this.chartDateAxis(chart);

            this.chartValueAxis(chart);

            this.chartSeries(chart);

            this.chart = chart;
        });
    }

    public chartDateAxis(chart) {
        const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
        dateAxis.periodChangeDateFormats.setKey("month", "MMM");
        dateAxis.renderer.grid.template.location = 0;
        dateAxis.renderer.minGridDistance = 20;
        dateAxis.renderer.grid.template.disabled = true;
        dateAxis.renderer.labels.template.fill = am4core.color("#225E66");
        dateAxis.renderer.labels.template.adapter.add("fill", (fill: any, target: { currentText: string }) => {
            if (this.monthsWithoutReadings > 1 && this.month >= 3 && this.month < 11) {
                if (
                    (target.currentText && target.currentText.toUpperCase() === this.endEstimatedMonth.toUpperCase()) ||
                    this.allEstimated
                ) {
                    this.canPaint = true;
                    return am4core.color(this.chartColorGreen);
                } else if (target.currentText && target.currentText.toUpperCase() === this.startEstimatedMonth.toUpperCase()) {
                    this.canPaint = false;
                    return am4core.color(this.chartColorGreen);
                } else if (this.canPaint) {
                    return am4core.color(this.chartColorGreen);
                } else {
                    return am4core.color(this.chartColor);
                }
            } else {
                return am4core.color(this.chartColorGreen);
            }
        });
    }

    public chartValueAxis(chart) {
        const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
        let lastValue = 0;
        let minValue = 0;
        chart.data.map(data => {
            if (data.total > lastValue) {
                lastValue = data.total;
            }
            if (data.total < minValue) {
                minValue = data.total;
            }
            if (lastValue < this.valueContaCerta) {
                lastValue = this.valueContaCerta;
            }
        });
        valueAxis.max = lastValue;
        valueAxis.min = minValue;
        valueAxis.renderer.labels.template.disabled = true;
        valueAxis.renderer.grid.template.disabled = true;
        valueAxis.renderer.baseGrid.disabled = true;
        valueAxis.renderer.labels.template.fill = am4core.color("#7C9599");
        valueAxis.renderer.labels.template.fontWeight = "bold";

        this.chartRanges(valueAxis);
    }

    public chartRanges(valueAxis) {
        if (this.month !== 1) {
            // Create the range
            const range = valueAxis.axisRanges.create();
            range.value = this.valueContaCertaMedia;
            range.grid.stroke = am4core.color(this.chartColor);
            range.grid.strokeWidth = 1;
            range.grid.strokeOpacity = 1;
            range.label.text = this.valueContaCertaMedia.toString().replace(".", ",") + "€";
            range.label.fill = am4core.color(this.chartColor);

            // Create the second range
            const range2 = valueAxis.axisRanges.create();
            range2.value = this.valueContaCerta;
            range2.grid.stroke = am4core.color(this.chartColor);
            range2.grid.strokeWidth = 1;
            range2.grid.strokeOpacity = 1;
            range2.grid.strokeDasharray = "6";
            range2.label.text = this.valueContaCerta.toString().replace(".", ",") + "€";
            range2.label.fill = am4core.color(this.chartColor);
        }
    }

    public chartSeries(chart) {
        // Create series
        const series = chart.series.push(new am4charts.ColumnSeries());
        series.dataFields.valueY = "total";
        series.dataFields.dateX = "date";
        series.columns.template.fillOpacity = 0.8;
        series.fill = am4core.color(this.chartColorGreen);
        // Column Template
        series.columns.template.strokeOpacity = 0;
        const columnTemplate = series.columns.template;
        columnTemplate.width = 10;
        columnTemplate.column.cornerRadius(20, 20, 20, 20);
    }

    public getData() {
        if (this.consumptionsObject && this.consumptionsObject.consumptionsChart.length !== 0 && this.month !== 1) {
            return this.consumptionsObject.consumptionsChart;
        } else {
            return this.getDummyData();
        }
    }

    public getDummyData() {
        const array = Array.apply(null, Array(6)).map((c, index) => {
            const date = moment().subtract(index, "months");
            return {
                date: date.format("YYYY-MM"),
                total: 50,
            };
        });
        return array.reverse();
    }

    public setLabelbyMonth() {
        switch (this.month) {
            case 1:
                this.label = this.error
                    ? "pt.edp.edponline.conta.certa.widget.error"
                    : "pt.edp.edponline.endpoint.conta.certa.label.first.month";
                break;
            case 2:
                this.label = "pt.edp.edponline.endpoint.conta.certa.label.second.month";
                break;
            case 11:
                this.label = this.finalValueAcertoPositive
                    ? "pt.edp.edponline.endpoint.conta.certa.label.eleven.month.pay"
                    : "pt.edp.edponline.endpoint.conta.certa.label.eleven.month.recieve";
                break;
            case 12:
                this.labelMonthTwelveTitle = this.finalValueAcertoPositive
                    ? "pt.edp.edponline.endpoint.conta.certa.label.twelve.month.title.pay"
                    : "pt.edp.edponline.endpoint.conta.certa.label.twelve.month.title.recieve";
                this.labelMonthTwelveValueDescription = this.finalValueAcertoPositive
                    ? "pt.edp.edponline.endpoint.conta.certa.label.twelve.month.value.description.add"
                    : "pt.edp.edponline.endpoint.conta.certa.label.twelve.month.value.description.remove";
                break;
        }
        this.labelMiddleMonths = this.finalValuePositive
            ? "pt.edp.edponline.endpoint.conta.certa.label.middle.months.label.higher"
            : "pt.edp.edponline.endpoint.conta.certa.label.middle.months.label.less";
        this.labelMiddleMonthsSecondLabel = this.finalValueAcertoPositive
            ? "pt.edp.edponline.endpoint.conta.certa.label.middle.months.second.label.pay"
            : "pt.edp.edponline.endpoint.conta.certa.label.middle.months.second.label.recieve";
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.zone.runOutsideAngular(() => {
            if (this.chart) {
                this.chart.dispose();
            }
        });
    }

    public reviewMonthPayment() {
        const modalOptions: NgbModalOptions = {
            windowClass: "edp-dialog-fullscreen",
        };

        const modalData: IModalData = {
            title: "",
            showClose: true,
        };

        this.edpModalService.custom(EdpContaCertaModalComponent, modalOptions, modalData).then(() => {
            /* Intentional resolve */
        });
    }
}
